import clsx from "clsx"
import { useEffect, useRef, useState } from "react"

import useDebounce from "@hooks/useDebounce"

import { faIcon } from "@utils/faIcon"

import { Icon, IconButton } from "@atoms"

import { CLEAR_SEARCH_EVENT } from "@constants/events"

import styles from "./SearchInput.module.scss"
import { SearchInputProps } from "./SearchInput.types"

const MIN_SEARCH_LENGTH = 2

export default function SearchInput(props: SearchInputProps) {
    const { size = "md", defaultValue, onValueChange, isDisabled = false, _isFocused, _isHovered } = props

    const [value, setValue] = useState<string>(defaultValue)
    const debouncedValue = useDebounce<string>(value, 500)

    const inputRef = useRef<HTMLInputElement>(null)

    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value)
    }

    const handleClear = () => {
        setValue("")
        if (!isDisabled) {
            inputRef.current?.focus()
        }
    }

    useEffect(() => {
        setValue(defaultValue)
    }, [defaultValue])

    useEffect(() => {
        if (debouncedValue.length >= MIN_SEARCH_LENGTH) {
            onValueChange(debouncedValue)
        } else {
            onValueChange("")
        }
    }, [debouncedValue])

    useEffect(() => {
        window.addEventListener(CLEAR_SEARCH_EVENT, handleClear)

        return () => {
            window.removeEventListener(CLEAR_SEARCH_EVENT, handleClear)
        }
    }, [])

    return (
        <div
            className={clsx(styles.base, styles[`${size}Size`], {
                [styles.typed]: debouncedValue.length > 0,
                [styles.typing]: debouncedValue.length === 0 && value.length > 0,
                [styles.disabled]: isDisabled,
                [styles.focused]: _isFocused,
                [styles.hovered]: _isHovered,
            })}
            aria-disabled={isDisabled}
        >
            <div className={styles.iconContainer}>
                <Icon
                    icon={faIcon({ name: "magnifying-glass", style: "regular" })}
                    size={14}
                    className={styles.icon}
                />
            </div>

            <input
                value={value}
                onChange={onInputChange}
                placeholder="Keyword search"
                className={styles.input}
                ref={inputRef}
                disabled={isDisabled}
                aria-disabled={isDisabled}
            />

            {value && !isDisabled && (
                <IconButton
                    size={size === "sm" ? "lg" : "xl"}
                    variant="ghost"
                    className={styles.clearButton}
                    icon={faIcon({ name: "circle-xmark", style: "solid" })}
                    onClick={handleClear}
                    aria-label="Clear input"
                    noDebounce={true}
                />
            )}
        </div>
    )
}
